import { LogoMark } from "../components/Logo";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { buildQuotePdf } from "../lib/quotePdf";
import { getProfession, cap, singularProc } from "../lib/professions";
import { formatCurrency } from "../lib/api";
import { FilePdf } from "@phosphor-icons/react";
import { PaymentOptions } from "../components/QuoteExtras";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

async function loadLogo(token) {
  try {
    const r = await axios.get(`${API}/public/quotes/${token}/logo`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = url; });
    const c = document.createElement("canvas"); c.width = img.naturalWidth; c.height = img.naturalHeight;
    c.getContext("2d").drawImage(img, 0, 0);
    return { dataUrl: c.toDataURL("image/png"), width: img.naturalWidth, height: img.naturalHeight };
  } catch { return null; }
}

export default function QuotePublicPage() {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [logo, setLogo] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/quotes/${token}`).then(async (r) => {
      setQuote(r.data);
      if (r.data.has_logo) setLogo(await loadLogo(token));
    }).catch(() => setError(true));
  }, [token]);

  if (error) return <div className="min-h-screen flex items-center justify-center text-slate-600" data-testid="public-quote-error">Orçamento não encontrado ou link inválido.</div>;
  if (!quote) return <div className="min-h-screen flex items-center justify-center text-slate-500">Carregando orçamento...</div>;
  const prof = getProfession(quote.profession);

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="public-quote-page">
      <div className="max-w-3xl mx-auto">
        <div className="card-crisp bg-white p-6 md:p-10">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-slate-900" data-testid="public-quote-clinic">{quote.clinic_name || "Orçamento"}</h1>
              <p className="text-sm text-slate-500 mt-1">Orçamento Nº {quote.quote_number} · {new Date(quote.created_at).toLocaleDateString("pt-BR")}</p>
            </div>
            {logo && <img src={logo.dataUrl} alt="Logo" className="max-h-16 max-w-[180px] object-contain" />}
          </div>

          <div className="mt-6 rounded-lg bg-slate-50 p-4 text-sm">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1" data-testid="public-client-label">{cap(prof.client)}</div>
            <div className="font-semibold text-slate-900">{quote.patient?.name}</div>
            {quote.patient?.phone && <div className="text-slate-600">{quote.patient.phone}</div>}
          </div>

          <table className="w-full text-sm mt-6">
            <thead><tr className="text-left text-slate-500 border-b border-slate-200"><th className="py-2 font-medium">{singularProc(prof)}</th><th className="py-2 font-medium text-center">Qtd</th><th className="py-2 font-medium text-right">Valor</th></tr></thead>
            <tbody>
              {quote.items.map((it, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-3"><div className="font-medium text-slate-900">{it.name}</div><div className="text-xs text-slate-500">{it.category}</div></td>
                  <td className="py-3 text-center font-mono-nums">{it.quantity}</td>
                  <td className="py-3 text-right font-mono-nums">{formatCurrency(it.line_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mt-6 ml-auto max-w-xs space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-600">Subtotal</span><span className="font-mono-nums">{formatCurrency(quote.subtotal)}</span></div>
            {quote.discount_amount > 0 && <div className="flex justify-between text-emerald-700"><span>Desconto ({quote.discount_percentage}%)</span><span className="font-mono-nums">- {formatCurrency(quote.discount_amount)}</span></div>}
            <div className="flex justify-between items-baseline pt-2 border-t border-slate-200"><span className="font-heading font-bold text-lg">Total</span><span className="font-mono-nums font-extrabold text-3xl text-indigo-700" data-testid="public-quote-total">{formatCurrency(quote.total)}</span></div>
            {quote.payment_method && <div className="text-right text-xs text-slate-500">Forma de pagamento: <strong>{quote.payment_method}</strong></div>}
          </div>

          <PaymentOptions options={quote.payment_options} testId="public-payment-options" />
          {quote.notes && <div className="mt-6 text-sm text-slate-700 whitespace-pre-wrap border-t border-slate-100 pt-4">{quote.notes}</div>}

          <div className="mt-8 flex flex-wrap gap-3">
            <Button onClick={() => buildQuotePdf(quote, logo, quote.clinic_name)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="public-quote-pdf"><FilePdf size={18} className="mr-2" /> Baixar PDF</Button>
            {quote.patient?.phone && <Button asChild variant="outline"><a href={`https://wa.me/?text=${encodeURIComponent(`Olá! Tenho interesse no orçamento ${quote.quote_number}.`)}`} target="_blank" rel="noreferrer">Falar com a clínica</a></Button>}
          </div>
        </div>
        <p className="text-center text-xs text-slate-400 mt-6 flex items-center justify-center gap-1.5"><LogoMark size={16} /> Orçamento gerado com O Seu Precificador</p>
      </div>
    </div>
  );
}
